import useTitanApp from "@/hooks/useTitanApp";
import { useEffect } from "react";
interface FormField {
  id: string;
  label: string;
  type: "text" | "date" | "time" | "select" | "radio" | "checkbox" | "textarea";
  options?: Array<{ value: string; label: string }>;
  visible?: boolean;
  conditional?: (formData: any) => boolean;
}

interface FormSection {
  id: string;
  title: string;
  fields: FormField[];
  visible?: boolean;
  conditional?: (formData: any) => boolean;
}

export interface FormTemplate {
  sections: FormSection[];
  selectOptions: {
    [key: string]: Array<{ value: string; label: string }>;
  };
  messages: {
    dataSharingAlert: {
      title: string;
      points: string[];
    };
    forwardOutOfStockInfo: string;
    adviceReminder: string;
    planAndActionsNote: string;
    internalNotesNote: string;
  };
  externalLinks: {
    ncrs: string;
    epsTracker: string;
  };
  labels: {
    [key: string]: string;
  };
  notSuppliedReasons: {
    [key: string]: string;
  };
  outcomeLabels: {
    [key: string]: string;
  };
}

export interface FormattedOutput {
  sections: Array<{
    title: string;
    fields: Array<{
      label: string;
      value: string | boolean;
    }>;
  }>;
}

export const getNhs111EmergencySupplyFormData = (): FormTemplate => {
  return {
    sections: [
      {
        id: "referral-details",
        title: "Referral Details",
        visible: true,
        fields: [
          {
            id: "referralOrigin",
            label: "Referral Origin",
            type: "select",
            visible: true,
          },
          {
            id: "referralOriginOther",
            label: "Please specify referral origin",
            type: "text",
            conditional: (data) => data.referralOrigin === "other",
          },
          {
            id: "referralContactDetails",
            label: "Referral Contact Details",
            type: "text",
            visible: true,
          },
          {
            id: "referralDate",
            label: "Date of Referral",
            type: "date",
            visible: true,
          },
          {
            id: "referralTime",
            label: "Time of Referral",
            type: "time",
            visible: true,
          },
        ],
      },
      {
        id: "patient-consent",
        title: "Patient Consent",
        visible: true,
        fields: [
          {
            id: "patientConsent",
            label: "Does the patient consent to this emergency supply?",
            type: "radio",
            options: [
              { value: "yes", label: "Yes" },
              { value: "no", label: "No" },
            ],
            visible: true,
          },
        ],
      },
      {
        id: "consultation-details",
        title: "Consultation Details",
        conditional: (data) => data.patientConsent === "yes",
        fields: [
          {
            id: "consultationDate",
            label: "Date",
            type: "date",
            visible: true,
          },
          {
            id: "consultationTime",
            label: "Time",
            type: "time",
            visible: true,
          },
          {
            id: "consultationMethod",
            label: "Method of Consultation",
            type: "select",
            visible: true,
          },
          {
            id: "patientAccompanied",
            label: "Patient Accompanied",
            type: "radio",
            options: [
              { value: "yes", label: "Yes" },
              { value: "no", label: "No" },
            ],
            visible: true,
          },
          {
            id: "accompaniedRelationship",
            label: "Relationship",
            type: "select",
            conditional: (data) => data.patientAccompanied === "yes",
          },
          {
            id: "accompaniedRelationshipOther",
            label: "Please specify relationship",
            type: "text",
            conditional: (data) =>
              data.patientAccompanied === "yes" &&
              data.accompaniedRelationship === "other",
          },
        ],
      },
      {
        id: "clinical-checks",
        title: "Clinical Checks",
        conditional: (data) => data.patientConsent === "yes",
        fields: [
          {
            id: "reasonForRequest",
            label: "Reason for request",
            type: "select",
            visible: true,
          },
          {
            id: "reasonForRequestOther",
            label: "Please specify reason",
            type: "text",
            conditional: (data) => data.reasonForRequest === "other",
          },
          {
            id: "ncrsRecordAccessed",
            label: "Has the patient's NCRS record been accessed?",
            type: "radio",
            options: [
              { value: "yes", label: "Yes" },
              { value: "no", label: "No" },
            ],
            visible: true,
          },
          {
            id: "electronicPrescriptionAvailable",
            label:
              "Does the patient have an outstanding electronic prescription available?",
            type: "radio",
            options: [
              { value: "yes", label: "Yes" },
              { value: "no", label: "No" },
            ],
            visible: true,
          },
        ],
      },
      {
        id: "medicine-supply",
        title: "Medicine Supply",
        conditional: (data) => data.patientConsent === "yes",
        fields: [
          {
            id: "emergencySupplyMedications",
            label: "Emergency Supply Medications",
            type: "text",
            visible: true,
          },
        ],
      },
      {
        id: "forward-out-of-stock",
        title: "Forward out of stock medication(s)",
        conditional: (data) => data.patientConsent === "yes",
        fields: [
          {
            id: "forwardOutOfStock",
            label:
              "Do you want to forward out of stock medication to another pharmacy?",
            type: "radio",
            options: [
              { value: "yes", label: "Yes" },
              { value: "no", label: "No" },
            ],
            conditional: (data) => data.hasOutOfStockMedications === true,
          },
          {
            id: "forwardedPharmacy",
            label: "Selected Pharmacy for Referral",
            type: "text",
            conditional: (data) => data.forwardOutOfStock === "yes",
          },
        ],
      },
      {
        id: "outcomes",
        title: "Outcomes",
        conditional: (data) => data.patientConsent === "yes",
        fields: [
          {
            id: "outcomes",
            label: "Outcome",
            type: "select",
            visible: true,
          },
          {
            id: "outcomesOther",
            label: "Please specify outcome",
            type: "text",
            conditional: (data) => data.outcomes === "other",
          },
        ],
      },
      {
        id: "referral-details-outcome",
        title: "Referral Details",
        conditional: (data) =>
          data.patientConsent === "yes" &&
          (data.outcomes === "non-urgent-signposting" ||
            data.outcomes === "urgent-escalation"),
        fields: [
          {
            id: "referralDestination",
            label: "Referral destination",
            type: "select",
            conditional: (data) => data.outcomes === "non-urgent-signposting",
          },
          {
            id: "referralDestinationOther",
            label: "Please specify referral destination",
            type: "text",
            conditional: (data) =>
              data.outcomes === "non-urgent-signposting" &&
              data.referralDestination === "other",
          },
          {
            id: "escalationDestination",
            label: "Escalation to",
            type: "select",
            conditional: (data) => data.outcomes === "urgent-escalation",
          },
          {
            id: "escalationDestinationOther",
            label: "Please specify escalation destination",
            type: "text",
            conditional: (data) =>
              data.outcomes === "urgent-escalation" &&
              data.escalationDestination === "other",
          },
          {
            id: "onwardReferralReason",
            label: "Referral reason",
            type: "textarea",
            conditional: (data) =>
              data.outcomes === "non-urgent-signposting" ||
              data.outcomes === "urgent-escalation",
          },
          {
            id: "onwardReferralDate",
            label: "Referral date",
            type: "date",
            conditional: (data) =>
              data.outcomes === "non-urgent-signposting" ||
              data.outcomes === "urgent-escalation",
          },
          {
            id: "onwardReferralTime",
            label: "Referral time",
            type: "time",
            conditional: (data) =>
              data.outcomes === "non-urgent-signposting" ||
              data.outcomes === "urgent-escalation",
          },
          {
            id: "destinationOdsCode",
            label: "ODS code",
            type: "text",
            conditional: (data) =>
              data.outcomes === "non-urgent-signposting" ||
              data.outcomes === "urgent-escalation",
          },
        ],
      },
      {
        id: "advice-and-support",
        title: "Advice and Support",
        conditional: (data) => data.patientConsent === "yes",
        fields: [
          {
            id: "adviceGiven",
            label: "Patient has been given this advice",
            type: "checkbox",
            visible: true,
          },
          {
            id: "adviceProvidedTo",
            label: "Who was the advice provided to?",
            type: "select",
            conditional: (data) => data.adviceGiven === true,
          },
          {
            id: "planAndRequestedActions",
            label: "Plan & requested actions",
            type: "textarea",
            visible: true,
          },
        ],
      },
      {
        id: "notes",
        title: "Notes",
        conditional: (data) => data.patientConsent === "yes",
        fields: [
          {
            id: "internalNotes",
            label: "Internal notes",
            type: "textarea",
            visible: true,
          },
        ],
      },
    ],
    selectOptions: {
      referralOrigins: [
        { value: "nhs-111-service", label: "NHS 111 Service" },
        { value: "nhs-111-online", label: "NHS 111 Online" },
        { value: "ambulance-service", label: "Ambulance Service" },
        { value: "gp-practice", label: "GP Practice" },
        { value: "gp-practice-online", label: "GP Practice (online)" },
        { value: "emergency-department", label: "Emergency Department" },
        { value: "urgent-treatment-centre", label: "Urgent Treatment Centre" },
        { value: "walk-in-centre", label: "Walk in Centre" },
        { value: "other", label: "Other" },
      ],
      consultationMethods: [
        { value: "face-to-face", label: "Face to Face" },
        { value: "telephone", label: "Telephone" },
        { value: "video", label: "Video" },
      ],
      accompaniedRelationships: [
        { value: "parent", label: "Parent" },
        { value: "guardian", label: "Guardian" },
        { value: "relative", label: "Relative" },
        { value: "carer", label: "Carer" },
        { value: "advocate", label: "Advocate" },
        { value: "other", label: "Other" },
      ],
      reasonsForRequest: [
        {
          value: "not-ordered",
          label: "Patient had not ordered their prescription",
        },
        {
          value: "not-ready",
          label: "Patient had ordered their prescription, but it was not ready",
        },
        { value: "lost-form", label: "Patient had lost prescription form" },
        {
          value: "lost-medicine",
          label: "Patient had lost the medicine(s) or appliance(s)",
        },
        {
          value: "unable-collect",
          label:
            "Patient was not able to collect the medicine(s) or appliance(s) from their usual pharmacy",
        },
        {
          value: "away-from-home",
          label:
            "Patient is away from home and had forgotten to/did not bring sufficient supplies of their medicine(s) or appliance(s)",
        },
        { value: "other", label: "Other" },
      ],
      outcomes: [
        { value: "advice-given", label: "Advice given only" },
        {
          value: "onward-referral",
          label: "Onward referral to another pharmacy",
        },
        {
          value: "non-urgent-signposting",
          label: "Non-urgent signposting to another service",
        },
        {
          value: "urgent-escalation",
          label: "Urgent escalation to another service",
        },
        { value: "other", label: "Other" },
      ],
      referralDestinations: [
        { value: "gp-practice", label: "GP Practice" },
        { value: "out-of-hours-gp", label: "Out of hours GP" },
        { value: "other", label: "Other" },
      ],
      escalationDestinations: [
        { value: "gp-practice", label: "GP Practice" },
        { value: "out-of-hours-gp", label: "Out of hours GP" },
        { value: "utc", label: "UTC (Urgent Treatment Centre)" },
        { value: "999", label: "999" },
        { value: "ae", label: "A&E" },
        { value: "other", label: "Other" },
      ],
      adviceProvidedToOptions: [
        { value: "patient", label: "Patient" },
        { value: "advocate", label: "Patient's advocate" },
      ],
    },
    messages: {
      dataSharingAlert: {
        title: "Data Sharing Details",
        points: [
          "Consent is required for service provision",
          "If the patient/their advocate gives consent for data sharing, the information they provide will be passed to the patient's GP practice, NHS England and the NHS BSA.",
          "Consent is usually from the patient but this may be the advocate if one presents on behalf of or with the patient. If you are obtaining consent from a young person under the age of 16 please consider Gillick competency",
        ],
      },
      forwardOutOfStockInfo:
        "Where it is appropriate for an emergency supply to be made, but the medicine or appliance is not in stock at the pharmacy, with the agreement of the patient you can generate an onward referral to another pharmacy by clicking the button below. You will still be eligible for the service completion fee in this case.",
      adviceReminder:
        "Please remind your patient on the importance of ordering prescriptions in a timely manner from their GP practice and the benefits of electronic repeat dispensing (eRD) tick to confirm.",
      planAndActionsNote:
        "This will be sent to the GP but MUST NOT be used for communicating actions. Any actions must be communicated by usual business routes.",
      internalNotesNote:
        "This data will not flow to GP practices, and should be used for any notes you want to keep for your own internal pharmacy records.",
    },
    externalLinks: {
      ncrs: "https://www.nhsbsa.nhs.uk/nhs-prescription-services",
      epsTracker:
        "https://www.nhsbsa.nhs.uk/pharmacies-gp-practices-and-appliance-contractors/dispensing-contractors-information/eps-tracker",
    },
    labels: {
      referralDetails: "Referral Details",
      patientConsent: "Patient Consent",
      consultationDetails: "Consultation Details",
      clinicalChecks: "Clinical Checks",
      medicineSupply: "Medicine Supply",
      forwardOutOfStock: "Forward out of stock medication(s)",
      outcomes: "Outcomes",
      adviceAndSupport: "Advice and Support",
      notes: "Notes",
      referralOrigin: "Referral Origin",
      referralContactDetails: "Referral Contact Details",
      dateOfReferral: "Date of Referral",
      timeOfReferral: "Time of Referral",
      consultationDate: "Date",
      consultationTime: "Time",
      methodOfConsultation: "Method of Consultation",
      patientAccompanied: "Patient Accompanied",
      relationship: "Relationship",
      reasonForRequest: "Reason for request",
      ncrsRecordAccessed: "Has the patient's NCRS record been accessed?",
      electronicPrescriptionAvailable:
        "Does the patient have an outstanding electronic prescription available?",
      addMedicationItem: "Add Medication Item",
      emergencySupplyMedications: "Emergency Supply Medications",
      selectPharmacyForReferral: "Select Pharmacy for Referral",
      forwardOutOfStockQuestion:
        "Do you want to forward out of stock medication to another pharmacy?",
      pleaseSpecify: "Please specify",
      onwardReferralReason: "Onward Referral Reason",
      onwardReferralDate: "Onward Referral Date",
      onwardReferralTime: "Onward Referral Time",
      destinationOdsCode: "Destination ODS Code",
      whoWasAdviceProvidedTo: "Who was the advice provided to?",
      patientHasBeenGivenAdvice: "Patient has been given this advice",
      planAndRequestedActions: "Plan & requested actions",
      internalNotes: "Internal notes",
      optional: "(Optional)",
      doPatientConsentToEmergencySupply:
        "Does the patient consent to this emergency supply?",
      viewDataSharingDetails: "View Data Sharing Details",
      openNCRS: "Open NCRS",
      epsTracker: "EPS Tracker",
      changePharmacy: "Change Pharmacy",
      remove: "Remove",
      searchForPharmacy: "Search for Pharmacy",
      selected: "Selected",
      prescriptionPreview: "Prescription Preview",
      complete: "Complete",
      distance: "Distance",
      notSupplied: "Not Supplied",
      supplied: "Supplied",
    },
    notSuppliedReasons: {
      "schedule-controlled":
        "A: Item not able to be supplied under emergency supply regulations (e.g. Schedule 1, 2 or 3 controlled drug)",
      "eps-prescription": "B: EPS Prescription dispensed for patient",
      "not-necessary":
        "C: Pharmacist determined that supply not necessary (e.g. not clinically appropriate or concern about abuse of service)",
      "not-in-stock": "D: Item not in stock",
      "patient-bought": "F: Patient bought item",
    },
    outcomeLabels: {
      "advice-given": "Advice given only",
      "onward-referral": "Onward referral to another pharmacy",
      "non-urgent-signposting": "Non-urgent signposting to another service",
      "urgent-escalation": "Urgent escalation to another service",
      other: "Other",
    },
  };
};

export const formatFormDataAsTemplate = (
  formData: any,
  template: FormTemplate,
): FormattedOutput => {
  const formattedSections: FormattedOutput["sections"] = [];

  for (const section of template.sections) {
    // Check if section is visible
    if (section.visible === false) continue;
    if (section.conditional && !section.conditional(formData)) continue;

    const formattedFields: Array<{
      label: string;
      value: string | boolean;
    }> = [];

    for (const field of section.fields) {
      // Check if field is visible (default to true if not specified)
      const isVisible = field.visible !== false;
      // Check if conditional passes (default to true if not specified)
      const conditionsMet = !field.conditional || field.conditional(formData);

      // Skip field if not visible or conditions not met
      if (!isVisible || !conditionsMet) continue;

      const fieldValue = formData[field.id];

      // Skip empty values
      if (
        fieldValue === undefined ||
        fieldValue === "" ||
        (typeof fieldValue === "boolean" && !fieldValue)
      ) {
        continue;
      }

      let displayValue: string | boolean = fieldValue;

      // Convert value to label if options exist
      if (
        field.id !== "forwardedPharmacy" &&
        field.id !== "emergencySupplyMedications"
      ) {
        if (field.id === "referralOrigin") {
          const option = template.selectOptions.referralOrigins.find(
            (o) => o.value === fieldValue,
          );
          displayValue = option ? option.label : fieldValue;
        } else if (field.id === "consultationMethod") {
          const option = template.selectOptions.consultationMethods.find(
            (o) => o.value === fieldValue,
          );
          displayValue = option ? option.label : fieldValue;
        } else if (field.id === "accompaniedRelationship") {
          const option = template.selectOptions.accompaniedRelationships.find(
            (o) => o.value === fieldValue,
          );
          displayValue = option ? option.label : fieldValue;
        } else if (field.id === "reasonForRequest") {
          const option = template.selectOptions.reasonsForRequest.find(
            (o) => o.value === fieldValue,
          );
          displayValue = option ? option.label : fieldValue;
        } else if (field.id === "outcomes") {
          const option = template.selectOptions.outcomes.find(
            (o) => o.value === fieldValue,
          );
          displayValue = option ? option.label : fieldValue;
        } else if (field.id === "referralDestination") {
          const option = template.selectOptions.referralDestinations.find(
            (o) => o.value === fieldValue,
          );
          displayValue = option ? option.label : fieldValue;
        } else if (field.id === "escalationDestination") {
          const option = template.selectOptions.escalationDestinations.find(
            (o) => o.value === fieldValue,
          );
          displayValue = option ? option.label : fieldValue;
        } else if (field.id === "adviceProvidedTo") {
          const option = template.selectOptions.adviceProvidedToOptions.find(
            (o) => o.value === fieldValue,
          );
          displayValue = option ? option.label : fieldValue;
        }
      } else if (field.id === "forwardedPharmacy" && fieldValue) {
        displayValue = `${fieldValue.name}, ${fieldValue.address}, ${fieldValue.postcode}`;
      } else if (field.id === "emergencySupplyMedications") {
        // Count medications instead of showing them as string
        if (Array.isArray(fieldValue) && fieldValue.length > 0) {
          displayValue = `${fieldValue.length} medication(s)`;
        } else {
          continue;
        }
      }

      // Convert yes/no values to human readable format
      if (fieldValue === "yes" || fieldValue === "no") {
        displayValue = fieldValue === "yes" ? "Yes" : "No";
      }

      formattedFields.push({
        label: field.label,
        value: displayValue,
      });
    }

    if (formattedFields.length > 0) {
      formattedSections.push({
        title: section.title,
        fields: formattedFields,
      });
    }
  }

  return {
    sections: formattedSections,
  };
};

type Nhs111FormData = ReturnType<typeof getNhs111EmergencySupplyFormData>;
type Nhs111FormattedOutput = ReturnType<typeof formatFormDataAsTemplate>;

export const useNhs111EmergencySupplyForm = () => {
  const { patientState } = useTitanApp();
  const { addConsultationSummaryLoader } = patientState || {};
  return {
    addConsultationSummaryLoader,
  };
};
